import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import {
  Anchor,
  ButtonSecondary,
  PageLevelMessage,
} from '@americanexpress/dls-react';
import AppStatusTable from '../Sections/Table/AppStatusTable';
import SearchPage from '../Sections/Search/SearchPage';
import FaqsSections from '../Sections/FAQS/FaqsSections';
import ViewELetter from '../Sections/ELetter/ViewELetter';
import NoResultsPage from '../Sections/Search/NoResultsPage';
import { decodeJwtPayload } from '../../utils/APIs';
import {
  resetState,
  setActiveELetter,
  setConsentSavedBanner,
  setESignDisagreementBanner,
  setNoResults,
  setSessionExpiredBanner,
} from '../../duck';

const StatusSiteLanding = ({ lang }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const searchResults = useSelector((state) =>
    state.getIn(['modules', 'unify-status-site', 'general', 'searchResults']),
  );
  const activeELetter = useSelector((state) =>
    state.getIn(['modules', 'unify-status-site', 'general', 'activeELetter']),
  );
  const showESignDisagreementBanner = useSelector((state) =>
    state.getIn([
      'modules',
      'unify-status-site',
      'general',
      'showESignDisagreementBanner',
    ]),
  );
  const showConsentSavedBanner = useSelector((state) =>
    state.getIn([
      'modules',
      'unify-status-site',
      'general',
      'showConsentSavedBanner',
    ]),
  );
  const userSession = useSelector((state) =>
    state.getIn(['modules', 'unify-status-site', 'general', 'userSession']),
  );
  const showSessionExpiredBanner = useSelector((state) =>
    state.getIn([
      'modules',
      'unify-status-site',
      'general',
      'showSessionExpiredBanner',
    ]),
  );
  const noResults = useSelector((state) =>
    state.getIn(['modules', 'unify-status-site', 'general', 'noResults']),
  );

  const handleSessionExpiry = useCallback(() => {
    dispatch(resetState());
    dispatch(setSessionExpiredBanner(true));
  }, [dispatch]);

  useEffect(() => {
    if (!userSession) return undefined;

    const payload = decodeJwtPayload(userSession);
    if (!payload?.exp) return undefined;

    const expiresInMs = payload.exp * 1000 - Date.now();
    if (expiresInMs <= 0) {
      handleSessionExpiry();
      return undefined;
    }

    const timer = setTimeout(handleSessionExpiry, expiresInMs);
    return () => clearTimeout(timer);
  }, [userSession, handleSessionExpiry]);

  return (
    <>
      <Helmet
        htmlAttributes={{ lang }}
        title={intl.formatMessage({ id: 'welcome.text' })}
      />
      <div>
        <h1 className="heading-5 pad-2-b pad-2-t dls-bright-blue">
          Application Status
        </h1>
        {showSessionExpiredBanner && (
          <PageLevelMessage
            type="warning"
            dismissible={true}
            onDismiss={() => dispatch(setSessionExpiredBanner(false))}
            aria-live="polite"
          >
            {intl.formatMessage({
              id: 'session.expired',
              defaultMessage:
                'Your session has expired. Please search your application again.',
            })}
          </PageLevelMessage>
        )}
        {(showConsentSavedBanner || activeELetter?.showConsentBanner) && (
          <PageLevelMessage
            type="success"
            dismissible={true}
            onDismiss={() => {
              dispatch(setConsentSavedBanner(false));
              if (activeELetter) {
                dispatch(
                  setActiveELetter({
                    ...activeELetter,
                    showConsentBanner: false,
                  }),
                );
              }
            }}
            aria-live="polite"
          >
            {intl.formatMessage({
              id: 'eletter.consent.saved',
              defaultMessage: 'Your consent preference has been saved.',
            })}
          </PageLevelMessage>
        )}
        {showESignDisagreementBanner && (
          <PageLevelMessage
            type="success"
            dismissible={true}
            onDismiss={() => dispatch(setESignDisagreementBanner(false))}
            aria-live="polite"
          >
            {intl.formatMessage({
              id: 'esign.disagreement.submit.success',
              defaultMessage:
                'Your information has been updated in our systems. You will reveive a letter in the mail.',
            })}
          </PageLevelMessage>
        )}
        {!searchResults && !noResults && (
          <div id="application-status-search">
            <SearchPage lang={lang} />
          </div>
        )}
        {noResults && (
          <NoResultsPage onTryAgain={() => dispatch(setNoResults(false))} />
        )}
        {searchResults && !activeELetter && (
          <>
            <AppStatusTable statusData={searchResults} />
            <div className="pad-3-t" style={{ textAlign: 'center' }}>
              <ButtonSecondary size="sm" onClick={() => dispatch(resetState())}>
                {intl.formatMessage({ id: 'search.page.exit' })}
              </ButtonSecondary>
            </div>
          </>
        )}
        {activeELetter && (
          <ViewELetter
            communicationId={activeELetter.communicationId}
            pcn={activeELetter.pcn}
            authorizationToken={activeELetter.authorizationToken}
            showConsentBanner={activeELetter.showConsentBanner}
            onExit={() => dispatch(setActiveELetter(null))}
          />
        )}
        {!activeELetter && (
          <section
            className="pad-3-t"
            aria-label="Personal loan application link"
          >
            <Anchor
              href="https://americanexpress.com/apl"
              target="_blank"
              rel="noopener noreferrer"
              inline={true}
            >
              For American Express&reg; Personal Loan Applications - go to
              americanexpress.com/apl and use the &quot;Login to Check for an
              Offer&quot; link to jump back into your application.
            </Anchor>
          </section>
        )}
        <FaqsSections />
      </div>
    </>
  );
};

StatusSiteLanding.propTypes = {
  lang: PropTypes.string.isRequired,
};

export default StatusSiteLanding;
